import { hasCompleteHomeworkSchedules } from "../homeworkReleasePlan";
import styled from "styled-components";
import ReleaseTable from "./ReleaseTable";
import { ReleaseButton, ButtonDisabled } from "shared/components/Button";
import Modal from "shared/components/Modal";
import useHomeworkReleaseView from "features/homework/hooks/useHomworkReleaseView";
import HomeworkSettingModal from "./HomeworkSettingModal";
import HomeworkScheduleModal from "./HomeworkScheduleModal";
import { HomeworkSchedule } from "shared/types/Report";
import { useState, useEffect } from "react";
import LoadingSpinner from "shared/components/Spinner";
import HomeworkDateScheduleModal from "./HomeworkDateScheduleModal";

const HomeworkReleaseView = () => {
    const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
    const [bulkDays, setBulkDays] = useState<boolean[]>(new Array(7).fill(false));
    const [bulkInterval, setBulkInterval] = useState<number>(1);
    const [isOpenDateScheduleModal, setIsOpenDateScheduleModal] = useState(false);
    const [isOpenScheduleWarning, setIsOpenScheduleWarning] = useState(false);
    const [pendingScheduleType, setPendingScheduleType] = useState<'week' | 'date' | null>(null);
    
    const {branchList, selectedBranchList, folderList, selectedItems, isContentAvailable,
        effectiveDate, onEffectiveDateChangeHandler,
        onReportReleseClickHandler,
        onSelectReportClickHandler,
        onSelectLessonClickHandler,
        isOpenDateErrorModal, onDateErrorModalConfirmClickHandler,
        isOpenNullModal, onNullModalConfirmClickHandler,
        isOpenReleaseFailedModal, onFailedModalConfirmClickHandler,
        isOpenReleaseSucceedModal, onSucceedModalConfirmClickHandler,
        setting, setSetting, settingComplete, isOpenSettingModal, onCloseSettingModalClickHandler, onConfirmSettingModalClickHandler, onDeleteSettingClickHandler, 
        isOpenSettingWarningModal, onSettingWarningModalOpenClickHanlder,  onSettingWarningConfirmClickHandler, onSettingWarningModalCloseClickHandler, modal, isOpenModal,
        schedules, setSchedules, isLoading, releaseFailureMessage, pendingReleaseCount, onRetryReleaseClickHandler,
        setIsOpenNullModal
    } = useHomeworkReleaseView();

    useEffect(() => {
        setSchedules([]);
    }, [selectedItems]);

    function parseName(name: string) {
        // 문자+숫자 패턴을 모두 분리 (예: "Unit 10" → ["Unit ", 10])
        return name.match(/([a-zA-Z가-힣\s]+|\d+)/g)?.map(part => {
            const num = Number(part);
            return isNaN(num) ? part : num;
        }) ?? [name];
    }
    function compareNames(a: string, b: string) {
        const aParts = parseName(a);
        const bParts = parseName(b);
        const len = Math.max(aParts.length, bParts.length);
        for (let i = 0; i < len; i++) {
            const aVal = aParts[i];
            const bVal = bParts[i];
            if (aVal === undefined) return -1;
            if (bVal === undefined) return 1;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                if (aVal !== bVal) return aVal - bVal;
            } else if (typeof aVal === 'string' && typeof bVal === 'string') {
                if (aVal !== bVal) return aVal.localeCompare(bVal);
            } else {
                // 숫자 < 문자
                return typeof aVal === 'number' ? -1 : 1;
            }
        }
        // 완전히 같으면 바이트 길이로
        return new Blob([a]).size - new Blob([b]).size;
    }

    const handleScheduleModalOpen = () => {
        // 이미 날짜 지정 일정이 있으면 경고
        const hasDateSchedule = schedules.some(s => Array.isArray(s.dates) && s.dates.length > 0 && s.dates.some(date => date !== ""));
        if (hasDateSchedule) {
            setPendingScheduleType('week');
            setIsOpenScheduleWarning(true);
            return;
        }
        if (selectedItems.length === 0) {
            setIsOpenNullModal(true);
            return;
        }

        // 이미 스케줄이 있으면 그대로 사용, 없으면 초기화
        if (schedules.length === 0) {
            const sorted = [...selectedItems].sort((a, b) => compareNames(a.name, b.name));
            setBulkDays(new Array(7).fill(false));
            setBulkInterval(1);
            const initialSchedules = sorted.map((item, index) => ({
                id: item.id,
                dayOfWeek: new Array(7).fill(false),
                order: index,
                interval: 1
            }));
            setSchedules(initialSchedules);
        }
        setIsOpenScheduleModal(true);
    };

    const handleScheduleModalClose = () => {
        setIsOpenScheduleModal(false);
    };

    const handleScheduleConfirm = (updatedSchedules: HomeworkSchedule[]) => {
        // 날짜 지정 필드 비우기
        const cleared = updatedSchedules.map(s => ({
            ...s,
            dates: []
        }));
        setSchedules(cleared);
        if (cleared.length > 0) {
            setBulkInterval(cleared[0].interval || 1);
        }
    };

    const handleDateScheduleModalOpen = () => {
        // 이미 요일/주기 일정이 있으면 경고
        const hasWeekSchedule = schedules.some(s => Array.isArray(s.dayOfWeek) && s.dayOfWeek.some(Boolean));
        if (hasWeekSchedule) {
            setPendingScheduleType('date');
            setIsOpenScheduleWarning(true);
            return;
        }
        if (selectedItems.length === 0) {
            setIsOpenNullModal(true);
            return;
        }
        setIsOpenDateScheduleModal(true);
    };

    const handleDateScheduleModalClose = () => {
        setIsOpenDateScheduleModal(false);
    };

    // 날짜 지정 일정 저장 시
    const handleDateScheduleConfirm = (dateSchedules: HomeworkSchedule[]) => {
        // 요일/주기 필드 비우기
        const cleared = dateSchedules.map(s => ({
            ...s,
            dayOfWeek: new Array(7).fill(false),
            interval: 1
        }));
        setSchedules(cleared);
    };

    // 일정이 설정되었는지 확인하는 함수
    const isScheduleSet = () => hasCompleteHomeworkSchedules(selectedItems, schedules);

    // 일정관리 버튼 스타일 결정
    const getScheduleButtonStyle = () => {
        if (selectedItems.length === 0) {
            return { opacity: 0.5, cursor: 'not-allowed' };
        }
        if (isScheduleSet()) {
            return { backgroundColor: '#48BB78', color: 'white' };
        }
        return { backgroundColor: '#F56565', color: 'white' };
    };

    // 경고 모달 확인 시 기존 일정 초기화 후 해당 모달 오픈
    const handleScheduleWarningConfirm = () => {
        setIsOpenScheduleWarning(false);
        if (pendingScheduleType === 'week') {
            // 기존 일정 초기화 후 요일/주기 일정 모달 오픈
            setBulkDays(new Array(7).fill(false));
            setBulkInterval(1);
            setSchedules([...selectedItems].sort((a, b) => compareNames(a.name, b.name)).map((item, index) => ({
                id: item.id, dayOfWeek: new Array(7).fill(false), order: index, interval: 1,
            })));
            setIsOpenScheduleModal(true);
        } else if (pendingScheduleType === 'date') {
            setSchedules([]);
            setIsOpenDateScheduleModal(true);
        }
        setPendingScheduleType(null);
    };

    // 경고 모달 닫기
    const handleScheduleWarningClose = () => {
        setIsOpenScheduleWarning(false);
        setPendingScheduleType(null);
    };

    return(
        <Container>
            <PageHeader>
                <PageTitle>과제 배포</PageTitle>
                <PageDesc>클래스를 고르고 과제를 선택한 다음, 일정을 지정한 뒤 배포하세요.</PageDesc>
            </PageHeader>
            {isLoading && (
                <Modal isOpen={true} title="처리중 입니다" content={<LoadingSpinner width={80} />} onClose={() => {}} />
            )}
            <Modal isOpen={isOpenModal} title={modal.title} content={modal.content} onClose={modal.onConfirmClickHandler}/>
            <Modal isOpen={isOpenSettingWarningModal} title="WARNING" content="학습 설정 변경을 하시면 기존의 설정은 무시 됩니다" onConfirm={onSettingWarningConfirmClickHandler} onClose={onSettingWarningModalCloseClickHandler}/>
            <HomeworkSettingModal isOpen={isOpenSettingModal} isContentAvailable={isContentAvailable} setSetting={setSetting} setting={setting} onSettingCancelClickHandler={onCloseSettingModalClickHandler} onSettingInputCompleteClickHandler={onConfirmSettingModalClickHandler}/>
            <HomeworkScheduleModal 
                isOpen={isOpenScheduleModal}
                selectedItems={selectedItems}
                schedules={schedules}
                onClose={handleScheduleModalClose}
                onConfirm={handleScheduleConfirm}
                bulkDays={bulkDays}
                setBulkDays={setBulkDays}
                bulkInterval={bulkInterval}
                setBulkInterval={setBulkInterval}
            />
            <HomeworkDateScheduleModal
                isOpen={isOpenDateScheduleModal}
                selectedItems={selectedItems}
                schedules={schedules}
                onClose={handleDateScheduleModalClose}
                onConfirm={handleDateScheduleConfirm}
            />
            <Modal isOpen={isOpenDateErrorModal} title="ERROR" content="종료일이 시작일보다 빠를 수 없습니다" onClose={onDateErrorModalConfirmClickHandler}/>
            <Modal isOpen={isOpenNullModal} title="ERROR" content="배포할 과제를 선택 해주세요" onClose={onNullModalConfirmClickHandler}/>
            <Modal isOpen={isOpenReleaseSucceedModal} title="SUCCESS" content="과제 배포에 성공하였습니다" onClose={onSucceedModalConfirmClickHandler}/>
            <Modal isOpen={isOpenReleaseFailedModal} title="ERROR" content={releaseFailureMessage} onClose={onFailedModalConfirmClickHandler}/>
            <Modal
                isOpen={isOpenScheduleWarning}
                title="경고"
                content="이미 다른 일정 방식이 지정되어 있습니다. 진행하면 기존 일정이 모두 삭제됩니다. 계속 진행하시겠습니까?"
                onConfirm={handleScheduleWarningConfirm}
                onClose={handleScheduleWarningClose}
            />
            {pendingReleaseCount > 0 && (
                <div role="status">
                    완료되지 않은 배포 {pendingReleaseCount}건이 있습니다. 기존 선택과 일정으로 이어서 처리할 수 있습니다.
                    <button type="button" disabled={isLoading} onClick={onRetryReleaseClickHandler}>미완료 배포 재시도</button>
                </div>
            )}
            <ReleaseTable 
                onSelectBranchClickHandler={onSelectLessonClickHandler} 
                onSelectReportClickHandler={onSelectReportClickHandler} 
                onDeleteSettingClickHandler={onDeleteSettingClickHandler} 
                setting={settingComplete} 
                branchList={branchList} 
                selectedBranchList={selectedBranchList} 
                folderList={folderList} 
                selectedItems={selectedItems}
                onOpenSettingModalClickHandler={onSettingWarningModalOpenClickHanlder}
                effectiveDate={effectiveDate} 
                onEffectiveDateChangeHandler={onEffectiveDateChangeHandler}
                onOpenScheduleModalClickHandler={handleScheduleModalOpen}
                onOpenDateScheduleModalClickHandler={handleDateScheduleModalOpen}
                showEndDate={false}
                branchHeader="클래스"
                showBranchColumn={true}
                showSettingSection={true}
                showScheduleButtons={true}
            />
            
            <ActionBar>
                <ScheduleStatusText>
                    {selectedItems.length === 0 ? (
                        "클래스와 과제를 선택하면 배포할 수 있습니다"
                    ) : isScheduleSet() ? (
                        <>일정관리 상태: <StatusSuccess>완료</StatusSuccess></>
                    ) : (
                        <>일정관리 상태: <StatusWarning>미완료 — 일괄일정 또는 날짜지정이 필요합니다</StatusWarning></>
                    )}
                </ScheduleStatusText>
                {isLoading || selectedBranchList.length === 0 || selectedItems.length === 0 || !isScheduleSet() ? (
                    <ButtonDisabled 
                        name="배포 (일정관리 필요)" 
                        onButtonClick={() => {}} 
                    />
                ) : (
                    <ReleaseButton 
                        name="배포" 
                        onButtonClick={onReportReleseClickHandler}
                    />
                )}
            </ActionBar>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    flex-grow: 1;
    padding: 24px 28px;
    background: #F8FAFC;
`

const PageHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const PageTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #0F172A;
    letter-spacing: -0.02em;
`

const PageDesc = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748B;
`

const ActionBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 20px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
`

const ScheduleStatusText = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
`

const StatusSuccess = styled.span`
    color: #48BB78;
    font-weight: 600;
    margin-left: 8px;
`

const StatusWarning = styled.span`
    color: #F56565;
    font-weight: 600;
    margin-left: 8px;
`

export default HomeworkReleaseView;